import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { type AuthState, ReactNativeMessageType } from 'types';
import { invokeRNMessage, isMobileApp } from 'utils/common';
import type { Field } from './UploadFile';

const useFilePreview = ({ file, url, extension, auth }: { file: Field; url: string; extension: string; auth: AuthState | null }) => {
  const { t } = useTranslation();
  const [previewState, setPreviewState] = useState<{ open: boolean; url: Blob | string | null; loading: boolean }>({
    open: false,
    url: null,
    loading: false,
  });

  const handleOpenPreview = useCallback(async () => {
    if (isMobileApp()) {
      invokeRNMessage(ReactNativeMessageType.OpenFile, { file: { ...file, url, extension }, token: auth?.jwtToken ?? '' });
      return;
    }

    if (!url) {
      toast.error(t('user.detail.file.loadFailed'));
      return;
    }

    try {
      setPreviewState((prev) => ({ ...prev, loading: true }));
      const response = await fetch(url, { headers: { Authorization: auth?.jwtToken ?? '' } });

      if (!response.ok) throw new Error('Failed to open file');
      const blob = await response.blob();
      setPreviewState({ open: true, url: blob, loading: false });
    } catch {
      setPreviewState((prev) => ({ ...prev, loading: false }));
      toast.error(t('user.detail.file.loadFailed'));
    }
  }, [file, url, extension, auth?.jwtToken, t]);

  const handleClosePreview = useCallback(() => setPreviewState({ open: false, url: null, loading: false }), []);

  return { previewState, handleOpenPreview, handleClosePreview };
};

export { useFilePreview };
